import { h, defineComponent, ref, watch } from 'vue';
import { hasFeature, hasPermission, Permissions, user as authUser } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import NegativeResponsesTableCard from '@/_legacy/views/Analytics/Overview/NegativeResponsesTableCard.vue';
import { QuestionGroupBy } from '@/services/api/resources/analytics/types';
import {
  groupedNegativeResponsesListExportUrl,
  groupedQuestionList,
  SupportedExportFormat,
} from '@/services/api/resources/analytics';
import { handleNegativeResponseClick, redirectNegativeResponses } from '@/ui/pages/analytics/Overview/redirects';
import { useTask } from '@/ui/concerns';
import GroupTitleDecoder from '@/_legacy/views/Analytics/decoders/GroupTitleDecoder';
import { download } from '@/ui/helpers/uiHelper';
import { useQueries } from '@/ui/pages/analytics/Overview/useQueries';
import { parseInt10 } from '@/ui/helpers';

export const NegativeResponsesCard = defineComponent({
  name: 'NegativeResponsesCard',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    const groupNegativeResponses = ref<QuestionGroupBy>(QuestionGroupBy.Question);

    const {
      negativeResponseTableQuery,
    } = useQueries();

    const tableList = ref<unknown>([]);

    const getList = useTask(
      () => {
        initialLoading.value = true;

        return groupedQuestionList(groupNegativeResponses.value, negativeResponseTableQuery());
      },
      tableResponse => {
        tableList.value = tableResponse.data.map(result => ({
          label: GroupTitleDecoder(result.group),
          group: result.group,
          value: result.negativeResponseCount,
        }));

        initialLoading.value = false;
      },
    );
    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => groupedNegativeResponsesListExportUrl(
        groupNegativeResponses.value,
        { ...negativeResponseTableQuery(), perPage: parseInt10(process.env.VUE_APP_MAX_ANALYTICS_EXPORT!) },
        format as SupportedExportFormat,
        authUser.value.locale,
      ),
      download,
    );

    watch([
      () => negativeResponseTableQuery(),
      groupNegativeResponses,
    ], getList.run, { deep: true });

    getList.run();

    return function render() {
      //legacy, not compatible with Vue typings
      //@ts-ignore
      return h(NegativeResponsesTableCard, {
        props: {
          results: tableList.value,
          loading: initialLoading.value,
          groupBy: groupNegativeResponses.value,
        },
        on: {
          click: () => redirectNegativeResponses(negativeResponseTableQuery()),
          groupByChanged: (value: QuestionGroupBy) => {
            groupNegativeResponses.value = value;
          },
          exportClicked: (format: SupportedExportFormat) => exportData(format),
          rowClick: (
            { row, groupBy }: { row: { group: { id: number } }; groupBy: QuestionGroupBy },
          ) => handleNegativeResponseClick(negativeResponseTableQuery(), row, groupBy),
        },
      });
    };
  },
});
